import React from 'react';

/**
 * Модальное окно для создания новой зоны исследования
 * Пропсы:
 *  - isOpen: boolean — открыто ли окно
 *  - isSaving: boolean — идет ли сохранение
 *  - newZoneDescription: string — описание новой зоны
 *  - onChangeDescription: function — обработчик изменения описания
 *  - onSave: function — обработчик сохранения
 *  - onCancel: function — обработчик отмены
 */
const NewZoneModal = ({
  isOpen,
  isSaving,
  newZoneDescription,
  onChangeDescription,
  onSave,
  onCancel
}) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay">
      <div className="modal">
        <h3>Новая зона исследования</h3>
        <label>
          Описание (RU):
          <input
            type="text"
            value={newZoneDescription}
            onChange={e => onChangeDescription(e.target.value)}
            required
          />
        </label>
        <div className="modal-buttons">
          <button onClick={onSave} disabled={isSaving}>Сохранить</button>
          <button onClick={onCancel} disabled={isSaving}>Отмена</button>
        </div>
      </div>
    </div>
  );
};

/**
 * Модальное окно для редактирования описания существующей зоны
 * Пропсы:
 *  - isOpen: boolean — открыто ли окно
 *  - editZoneDescription: string — новое описание зоны
 *  - onChangeDescription: function — обработчик изменения описания
 *  - onSave: function — обработчик сохранения
 *  - onCancel: function — обработчик отмены
 *  - isSaving: boolean (опционально) — идет ли сохранение
 */
const EditZoneModal = ({
  isOpen,
  editZoneDescription,
  onChangeDescription,
  onSave,
  onCancel,
  isSaving
}) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay">
      <div className="modal">
        <h3>Редактировать описание зоны</h3>
        <label>
          Описание (RU):
          <input
            type="text"
            value={editZoneDescription}
            onChange={e => onChangeDescription(e.target.value)}
            required
          />
        </label>
        <div className="modal-buttons">
          <button onClick={onSave} disabled={isSaving}>Сохранить</button>
          <button onClick={onCancel} disabled={isSaving}>Отмена</button>
        </div>
      </div>
    </div>
  );
};

/**
 * Модальное окно для создания новой группы (папки) внутри зоны
 * Пропсы:
 *  - isOpen: boolean — открыто ли окно
 *  - parentGroup: объект — родительская группа (обязательно)
 *  - newGroupName: string — название новой группы
 *  - onChangeGroupName: function — обработчик изменения названия
 *  - onSave: function — обработчик сохранения
 *  - onCancel: function — обработчик отмены
 */
const CreateGroupModal = ({
  isOpen,
  parentGroup,
  newGroupName,
  onChangeGroupName,
  onSave,
  onCancel
}) => {
  if (!isOpen || !parentGroup) return null;
  return (
    <div className="modal-overlay">
      <div className="modal">
        <h3>Создание подгруппы</h3>
        <label>
          Родительская группа:
          <input type="text" value={parentGroup.group_description} disabled />
        </label>
        <label>
          Название новой группы:
          <input
            type="text"
            value={newGroupName}
            onChange={e => onChangeGroupName(e.target.value)}
            required
          />
        </label>
        <div className="modal-buttons">
          <button onClick={onSave}>Сохранить</button>
          <button onClick={onCancel}>Отмена</button>
        </div>
      </div>
    </div>
  );
};

/**
 * Модальное окно для редактирования группы (папки) или значения.
 * Позволяет редактировать только название (если это папка), либо название, значение и поле "В заключение" (если это значение).
 *
 * Пропсы:
 *  - isOpen: boolean — открыто ли окно
 *  - group: объект — редактируемая группа (обязательно)
 *  - groupName: string — новое название группы
 *  - onChangeGroupName: function — обработчик изменения названия
 *  - groupValue: string — новое значение (если есть)
 *  - onChangeGroupValue: function — обработчик изменения значения
 *  - groupReportValue: string — новое значение для поля "В заключение" (если есть)
 *  - onChangeGroupReportValue: function — обработчик изменения поля "В заключение"
 *  - onSave: function — обработчик сохранения
 *  - onCancel: function — обработчик отмены
 */
const EditGroupModal = ({
  isOpen,
  group,
  groupName,
  onChangeGroupName,
  groupValue,
  onChangeGroupValue,
  groupReportValue,
  onChangeGroupReportValue,
  onSave,
  onCancel,
  isNormal,
  onChangeIsNormal
}) => {
  if (!isOpen || !group) return null;
  const isValue = group.group_value !== null && group.group_value !== '';
  return (
    <div className="modal-overlay">
      <div className="modal">
        <h3>Редактировать группу</h3>
        <label>
          Название:
          <input
            type="text"
            value={groupName}
            onChange={e => onChangeGroupName(e.target.value)}
          />
        </label>
        {isValue && (
          <label>
            Значение:
            <textarea
              value={groupValue}
              onChange={e => onChangeGroupValue(e.target.value)}
              style={{ height: 64, width: '100%', fontSize: 15, borderRadius: 8, border: '1px solid #ccd1d9', padding: 8, resize: 'vertical', boxSizing: 'border-box' }}
            />
          </label>
        )}
        {isValue && (
          <label>
            В заключение:
            <textarea
              value={groupReportValue}
              onChange={e => onChangeGroupReportValue(e.target.value)}
              placeholder="(необязательно)"
              style={{ height: 64, width: '100%', fontSize: 15, borderRadius: 8, border: '1px solid #ccd1d9', padding: 8, resize: 'vertical', boxSizing: 'border-box' }}
            />
          </label>
        )}
        {isValue && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0' }}>
            <span>Норма</span>
            <input
              type="checkbox"
              checked={!!isNormal}
              onChange={e => onChangeIsNormal(e.target.checked)}
              style={{ width: 16, height: 16, margin: 0, padding: 0, cursor: 'pointer' }}
            />
          </div>
        )}
        <div className="modal-buttons">
          <button onClick={onSave}>Сохранить</button>
          <button onClick={onCancel}>Отмена</button>
        </div>
      </div>
    </div>
  );
};

/**
 * Модальное окно для добавления новой группы с описанием (значения).
 * Позволяет задать родительскую группу, название, значение и текст для заключения.
 *
 * Пропсы:
 *  - isOpen: boolean — открыто ли окно
 *  - parentGroup: объект — родительская группа (обязательно)
 *  - newGroupName: string — название новой группы
 *  - onChangeGroupName: function — обработчик изменения названия
 *  - newValue: string — значение
 *  - onChangeValue: function — обработчик изменения значения
 *  - newReportValue: string — текст для поля "В заключение"
 *  - onChangeReportValue: function — обработчик изменения поля "В заключение"
 *  - onSave: function — обработчик сохранения
 *  - onCancel: function — обработчик отмены
 */
const CreateValueModal = ({
  isOpen,
  parentGroup,
  newGroupName,
  onChangeGroupName,
  newValue,
  onChangeValue,
  newReportValue,
  onChangeReportValue,
  onSave,
  onCancel,
  isNormal,
  onChangeIsNormal
}) => {
  if (!isOpen || !parentGroup) return null;
  return (
    <div className="modal-overlay">
      <div className="modal">
        <h3>Добавить группу с описанием</h3>
        <label>
          Родительская группа:
          <input type="text" value={parentGroup.group_description} disabled />
        </label>
        <label>
          Название новой группы:
          <input
            type="text"
            value={newGroupName}
            onChange={e => onChangeGroupName(e.target.value)}
            required
          />
        </label>
        <label>
          Значение:
          <textarea
            value={newValue}
            onChange={e => onChangeValue(e.target.value)}
            required
            style={{ height: 64, width: '100%', fontSize: 15, borderRadius: 8, border: '1px solid #ccd1d9', padding: 8, resize: 'vertical', boxSizing: 'border-box' }}
          />
        </label>
        <label>
          В заключение:
          <textarea
            value={newReportValue}
            onChange={e => onChangeReportValue(e.target.value)}
            placeholder="(необязательно)"
            style={{ height: 64, width: '100%', fontSize: 15, borderRadius: 8, border: '1px solid #ccd1d9', padding: 8, resize: 'vertical', boxSizing: 'border-box' }}
          />
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0' }}>
          <span>Норма</span>
          <input
            type="checkbox"
            checked={!!isNormal}
            onChange={e => onChangeIsNormal(e.target.checked)}
            style={{ width: 16, height: 16, margin: 0, padding: 0, cursor: 'pointer' }}
          />
        </div>
        <div className="modal-buttons">
          <button onClick={onSave}>Сохранить</button>
          <button onClick={onCancel}>Отмена</button>
        </div>
      </div>
    </div>
  );
};

export { NewZoneModal, EditZoneModal, CreateGroupModal, EditGroupModal, CreateValueModal };
